import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { ClientList } from "../../types/client.types";
import { AppTheme } from "../../AppTheme";
import { getAllClients } from "../../service/client/ClientServices";
import { PaginationInputDto } from "../../model/common/PaginatedInputDto";
import { Toast } from "primereact/toast";
import { PermissionVariables } from "../../shared/constant/PermissionVariables";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPencil } from "@fortawesome/free-solid-svg-icons";
import { showToast } from "../../store/slices/toastSlice";
import { getInvoiceByClientId } from "../../service/invoice/InvoiceServices";
import { setError } from "../../store/slices/errorSlice";

export const ClientListPage: React.FC = () => {
  // Change the state type to ClientList instead of GetClientDto
  const [clients, setClients] = useState<ClientList[]>([]);
  const [selectedClients, setSelectedClients] = useState<ClientList[]>([]);
  const [filters, setFilters] = useState({
    global: { value: "", matchMode: FilterMatchMode.CONTAINS },
    client_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    contact_person: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    country_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: undefined,
    sortOrder: undefined,
  });

  const userPermissions = useSelector(
    (state: any) => state.permission.permissions
  );
  const localization = useSelector((state: any) => state.localization);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = React.useRef<Toast>(null);


  const fetchClients = async () => {
    try {
      const params: PaginationInputDto = {
        Page: lazyParams.page,
        Limit: lazyParams.rows,
        SortProperty: lazyParams.sortField,
        SortBy: lazyParams.sortOrder === 1 ? "ASC" : "DESC",
        Filter: globalFilterValue.length > 0 ? globalFilterValue : undefined,
        PropertyNames: [
          "client_name",
          "contact_person",
          "email",
          "country_name",
        ],
      };

      const response = await getAllClients(dispatch, params);
      // const invoices = await getInvoiceByClientId(dispatch,params,id)

      if (response) {
        setClients(response.List);
        setTotalRecords(response.TotalCount);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      dispatch(showToast({
        severity: "error",
        summary: localization?.Error || "Error",
        detail: localization?.FailedToLoadClients || "Failed to load clients"
      }));
    }
  };

  useEffect(() => {
    fetchClients();
  }, [lazyParams, globalFilterValue]);

  const onPage = (event: any) => {
    setLazyParams((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page + 1,
    }));
  };

  const onSort = (event: any) => {
    setLazyParams((prev) => ({
      ...prev,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  // Column Templates
  const nameBodyTemplate = (rowData: ClientList) => {
    return (
      <div>
        <span className="font-medium">{rowData.client_name}</span>
      </div>
    );
  };

  const contactPersonBodyTemplate = (rowData: ClientList) => {
    return (
      <div>
        <span>{rowData.primary_contact_person}</span>
      </div>
    );
  };

  const emailBodyTemplate = (rowData: ClientList) => {
    if (!rowData.email) return <div>-</div>;
    return (
      <div>
        <a
          href={`mailto:${rowData?.email}`}
          className="text-primary hover:underline"
        >
          {rowData?.email}
        </a>
      </div>
    );
  };

  const phoneBodyTemplate = (rowData: ClientList) => {
    return (
      <div>
        <span>{rowData.phone || "-"}</span>
      </div>
    );
  };

  const currencyBodyTemplate = (rowData: ClientList) => {
    if (!rowData.currency_code) return <div>-</div>; // Handle case where country is not available
    return (
      <div>
        <span>{rowData.currency_code}</span>
      </div>
    );
  };

  const actionBodyTemplate = (rowData: ClientList) => {
    return (
      <div className="flex gap-2">
        <Button
          className="p-button-rounded p-button-text border-1 border-gold"
          onClick={() => navigate(`/client/${rowData.client_id}`)}
        >
          <FontAwesomeIcon icon={faEye} />
          {"  "}
        </Button>

        {userPermissions?.includes(PermissionVariables.editClient) && (
          <Button
            className="p-button-rounded p-button-text border-1 border-gold"
            onClick={() => navigate(`/client/edit/${rowData.client_id}`)}
          >
            <FontAwesomeIcon icon={faPencil} />
            {"  "}
          </Button>
        )}
      </div>
    );
  };

  const header = (
    <div className="flex justify-between items-center gap-2 flex-wrap">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder={localization?.SearchClients || "Search Clients"}
        />
      </span>
      {userPermissions?.includes(PermissionVariables.addClient) && (
        <Button className="w-[135px]" icon="pi pi-plus" onClick={() => navigate("/client/create")}>
          {localization?.AddClient || "Add Client"}
        </Button>
      )}
    </div>
  );

  useEffect(() => {

  }, [])

  return (
    <div>
      <Toast ref={toast} />
      <div className="flex justify-between items-center mb-0">
        <h1 className="text-xl font-bold surface-900 mb-4">{localization?.Clients || "Clients"}</h1>
      </div>

      <Card className="card_content_padding">
        <DataTable
          value={clients}
          lazy
          paginator
          first={lazyParams.first}
          rows={lazyParams.rows}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          rowsPerPageOptions={[5, 10, 25, 50]}
          dataKey="client_id"
          filters={filters}
          globalFilterFields={[
            "client_name",
            "contact_person",
            "email",
            "country_name",
          ]}
          header={header}
          emptyMessage={localization?.NoClientsFound || "No clients found"}
          stripedRows
          className="p-datatable-sm"
        >
          <Column
            field="client_name"
            header={localization?.Name || "Name"}
            body={nameBodyTemplate}
            sortable
          />
          {/* <Column
            field="company_name"
            header="Company Name"
          /> */}
          <Column
            field="contact_person"
            header={localization?.ContactPerson || "Contact Person"}
            body={contactPersonBodyTemplate}
          />
          <Column
            field="email"
            header={localization?.Email || "Email"}
            body={emailBodyTemplate}
          />
          <Column
            field="phone"
            header={localization?.Phone || "Phone"}
            body={phoneBodyTemplate}
          />
          <Column
            field="currency_code"
            header={localization?.Currency || "Currency"}
            body={currencyBodyTemplate}
          />
          <Column
            body={actionBodyTemplate}
            header={localization?.Actions || "Actions"}
            style={{ width: "8rem", textAlign: "center" }}
          />
        </DataTable>
      </Card>
    </div>
  );
};